// ============================================
// PAGE - Recuperar Contraseña
// ============================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input } from '@almacenes/ui';
import { useRutFormatter } from '../../hooks/useRutFormatter';
import { useRequestPasswordReset, useVerifyPasswordResetCode, useConfirmPasswordReset } from '@almacenes/core';
import logo from '@/assets/logos/logo.svg';

export const RecoverPasswordPage = () => {
  const { formattedValue, rawValue, handleChange, isValid } = useRutFormatter();
  const { mutate: requestReset, isPending: isRequestPending } = useRequestPasswordReset();
  const { mutate: verifyCode, isPending: isVerifyPending } = useVerifyPasswordResetCode();
  const { mutate: confirmReset, isPending: isConfirmPending } = useConfirmPasswordReset();
  
  const [step, setStep] = useState<'request' | 'code' | 'newPassword' | 'success'>('request');
  const [userEmail, setUserEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    requestReset(rawValue, {
      onSuccess: (data) => {
        setUserEmail(data.email);
        setStep('code');
      },
      onError: (err: any) => {
        // El error viene transformado por axios-client con statusCode
        const statusCode = err?.statusCode || err?.response?.status;
        
        if (statusCode === 404) {
          setErrorMessage('No existe un almacén asociado a este RUT.');
        } else {
          setErrorMessage(err?.message || 'Error al solicitar el código');
        }
      },
    });
  };

  const handleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    verifyCode(
      { rutAlmacen: rawValue, code },
      {
        onSuccess: (data) => {
          if (data.valid) {
            setStep('newPassword');
          } else {
            setErrorMessage('El código no es válido. Revisa e inténtalo nuevamente.');
          }
        },
        onError: (err: any) => {
          setErrorMessage(err?.message || 'Error al verificar el código');
        },
      }
    );
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    confirmReset(
      { rutAlmacen: rawValue, code, newPassword },
      {
        onSuccess: () => {
          setStep('success');
        },
        onError: (err: any) => {
          setErrorMessage(err?.message || 'Error al cambiar la contraseña');
        },
      }
    );
  };

  const isFormValid = formattedValue.length > 0 && isValid();
  const isCodeValid = code.length === 6;
  const isPasswordValid = 
    newPassword.length >= 8 && 
    confirmPassword.length >= 8 && 
    newPassword === confirmPassword;

  return (
    <div className="min-h-screen bg-background">
      {/* Topbar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <img 
              src={logo} 
              alt="Almacenes Digitales" 
              className="h-9"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row min-h-[calc(100vh-73px)]">
        {/* Left Side - Image */}
        <div 
          className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #11194F 0%, #1A2570 50%, #2A3690 100%)'
          }}
        >
          {/* Círculos decorativos de fondo */}
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-white/15 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-primary/20 rounded-full blur-xl"></div>
          
          <div className="max-w-md text-center relative z-10">
            <div className="w-32 h-32 bg-white rounded-full mx-auto mb-6 flex items-center justify-center shadow-lg">
              <svg
                className="w-16 h-16 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
            <h2 className="text-title-2 mb-4 font-bold" style={{ color: 'white' }}>
              ¿Olvidaste tu contraseña?
            </h2>
            <p className="text-base" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              No te preocupes, ingresa el RUT de tu almacén y te enviaremos un código para restablecer el acceso.
            </p>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
          <div className="w-full max-w-md">
            {step === 'request' ? (
              <>
                <div className="mb-8 lg:hidden text-center">
                  <h2 className="text-title-2 text-text-primary mb-2">
                    Recuperar Contraseña
                  </h2>
                  <p className="text-body-secondary">
                    Ingresa el RUT de tu almacén
                  </p>
                </div>

                <div className="hidden lg:block mb-8">
                  <h2 className="text-title-2 text-text-primary mb-2">
                    Recuperar Contraseña
                  </h2>
                  <p className="text-body-secondary">
                    Ingresa el RUT de tu almacén para recibir las instrucciones
                  </p>
                </div>

                {/* Mensaje de error */}
                {errorMessage && (
                  <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 border border-red-200">
                    {errorMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* RUT Almacén */}
                  <div>
                    <Input
                      label="RUT del Almacén"
                      type="text"
                      placeholder="12.345.678-9"
                      required
                      value={formattedValue}
                      onChange={handleChange}
                      error={
                        formattedValue.length > 0 && !isValid()
                          ? 'RUT inválido'
                          : undefined
                      }
                    />
                    <p className="mt-2 text-sm text-text-tertiary">
                      Ingresa el RUT del almacén asociado a tu cuenta
                    </p>
                  </div>

                  {/* Botón Recuperar */}
                  <Button
                    variant="primary"
                    className="w-full py-3"
                    disabled={!isFormValid}
                    isLoading={isRequestPending}
                  >
                    Recuperar
                  </Button>

                  {/* Link volver al login */}
                  <div className="text-center pt-4">
                    <Link
                      to="/auth/login"
                      className="text-link hover:underline text-sm"
                    >
                      ← Volver al inicio de sesión
                    </Link>
                  </div>
                </form>
              </>
            ) : step === 'code' ? (
              <>
                <div className="mb-8">
                  <h2 className="text-title-2 text-text-primary mb-2">
                    Ingresa el Código
                  </h2>
                  <p className="text-body-secondary">
                    Hemos enviado un código de 6 dígitos a
                  </p>
                  <p className="text-body font-semibold mt-1">
                    {userEmail}
                  </p>
                </div>

                {/* Mensaje informativo */}
                <div className="mb-6 rounded-lg bg-blue-50 p-4 text-sm text-blue-800 border border-blue-200">
                  <p className="mb-2">
                    Revisa tu bandeja de entrada y tu carpeta de spam.
                  </p>
                  <p>
                    Si no es tu correo o cambiaste el correo, por favor{' '}
                    <a
                      href="mailto:soporte@almacenesdigitales.cl"
                      className="text-link hover:underline font-medium"
                    >
                      contáctanos
                    </a>
                    .
                  </p>
                </div>

                {/* Mensaje de error */}
                {errorMessage && (
                  <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 border border-red-200">
                    {errorMessage}
                  </div>
                )}

                <form onSubmit={handleCodeSubmit} className="space-y-6">
                  {/* Código de 6 dígitos */}
                  <div>
                    <Input
                      label="Código de Verificación"
                      type="text"
                      placeholder="000000"
                      required
                      value={code}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setCode(value);
                      }}
                      className="text-center text-2xl tracking-widest"
                    />
                    <p className="mt-2 text-sm text-text-tertiary text-center">
                      Ingresa el código de 6 dígitos
                    </p>
                  </div>

                  {/* Botón Verificar */}
                  <Button
                    variant="primary"
                    className="w-full py-3"
                    disabled={!isCodeValid}
                    isLoading={isVerifyPending}
                  >
                    Verificar Código
                  </Button>

                  {/* Link volver atrás */}
                  <div className="text-center pt-4">
                    <button
                      type="button"
                      onClick={() => setStep('request')}
                      className="text-link hover:underline text-sm"
                    >
                      ← Volver atrás
                    </button>
                  </div>
                </form>
              </>
            ) : step === 'newPassword' ? (
              <>
                <div className="mb-8">
                  <h2 className="text-title-2 text-text-primary mb-2">
                    Nueva Contraseña
                  </h2>
                  <p className="text-body-secondary">
                    Ingresa tu nueva contraseña
                  </p>
                </div>

                {/* Mensaje de error */}
                {errorMessage && (
                  <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 border border-red-200">
                    {errorMessage}
                  </div>
                )}

                <form onSubmit={handlePasswordSubmit} className="space-y-6">
                  {/* Nueva contraseña */}
                  <div>
                    <Input
                      label="Nueva Contraseña"
                      type="password"
                      placeholder="••••••••"
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      error={
                        newPassword.length > 0 && newPassword.length < 8
                          ? 'La contraseña debe tener al menos 8 caracteres'
                          : undefined
                      }
                    />
                  </div>

                  {/* Repetir contraseña */}
                  <div>
                    <Input
                      label="Repetir Contraseña"
                      type="password"
                      placeholder="••••••••"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      error={
                        confirmPassword.length > 0 && newPassword !== confirmPassword
                          ? 'Las contraseñas no coinciden'
                          : undefined
                      }
                    />
                  </div>

                  {/* Botón Cambiar Contraseña */}
                  <Button
                    variant="primary"
                    className="w-full py-3"
                    disabled={!isPasswordValid}
                    isLoading={isConfirmPending}
                  >
                    Cambiar Contraseña
                  </Button>

                  {/* Link volver atrás */}
                  <div className="text-center pt-4">
                    <button
                      type="button"
                      onClick={() => setStep('code')}
                      className="text-link hover:underline text-sm"
                    >
                      ← Volver atrás
                    </button>
                  </div>
                </form>
              </>
            ) : step === 'success' ? (
              <>
                <div className="text-center">
                  {/* Ícono de éxito */}
                  <div className="w-20 h-20 bg-green-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>

                  <h2 className="text-title-2 text-text-primary mb-3">
                    ¡Contraseña Actualizada!
                  </h2>
                  <p className="text-body-secondary mb-8">
                    Tu contraseña ha sido cambiada exitosamente. Ahora puedes iniciar sesión con tu nueva contraseña.
                  </p>

                  {/* Botón Ir al Login */}
                  <Link to="/auth/login">
                    <Button variant="primary" className="w-full py-3">
                      Ir al Inicio de Sesión
                    </Button>
                  </Link>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
